import { useState, useCallback, useRef } from 'react';
import type { ChatMessage, Language, RichContent } from '@pullman/shared';
import { useChatState } from './useChatState';
import { useChatStream } from './useChatStream';
import { useTranslation } from '@/i18n';

function generateId(): string {
  return crypto.randomUUID();
}

export function useChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId] = useState(() => generateId());
  const { chatState, activateChat, setChatState } = useChatState();
  const { sendStream, cancel } = useChatStream();
  const { language, setLanguage } = useTranslation();
  const streamingContentRef = useRef('');

  const sendMessage = useCallback(
    (text: string) => {
      if (!text.trim() || isStreaming) return;

      // Transition from landing to active
      if (chatState === 'landing') {
        activateChat();
      }

      // Add user message
      const userMsg: ChatMessage = {
        id: generateId(),
        role: 'user',
        content: text.trim(),
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);

      // Create placeholder for AI
      const aiId = generateId();
      const aiMsg: ChatMessage = {
        id: aiId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, aiMsg]);
      setIsStreaming(true);
      streamingContentRef.current = '';

      sendStream(sessionId, text.trim(), language, {
        onToken: (token: string) => {
          streamingContentRef.current += token;
          const currentContent = streamingContentRef.current;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === aiId ? { ...m, content: currentContent } : m,
            ),
          );
        },
        onRichContent: (richContent: RichContent) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === aiId ? { ...m, richContent } : m,
            ),
          );
        },
        onComplete: () => {
          setIsStreaming(false);
        },
        onError: (error: string) => {
          console.error('Chat error:', error);
          setIsStreaming(false);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === aiId
                ? { ...m, content: 'I apologize, but I encountered an issue. Please try again.' }
                : m,
            ),
          );
        },
      });
    },
    [isStreaming, chatState, activateChat, sendStream, sessionId, language],
  );

  const cancelStream = useCallback(() => {
    cancel();
    setIsStreaming(false);
  }, [cancel]);

  return {
    messages,
    sendMessage,
    isStreaming,
    chatState,
    setChatState,
    language,
    setLanguage,
    sessionId,
    cancelStream,
  };
}
